import React, { MutableRefObject, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Col, Dropdown, Row } from 'antd';
import { AxiosError } from 'axios';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import moment, { Moment } from 'moment-timezone';
import styled from 'styled-components';

import { ReactComponent as ArrowDownIcon } from 'app/assets/img/arrow-down-icon.svg';
import { ReactComponent as ExportExcel } from 'app/assets/img/export-excel-icon.svg';
import { ReactComponent as FilterIcon } from 'app/assets/img/filter-icon.svg';

import { Layout } from '../NewAccount/layouts';
import { selectTranslations } from '../App/slice/selectors';
import { ACCOUNT_MNG_MENU } from '../NewAccount/layouts/AccountManager';
import RangeDatePickerCustom from '../NewAccount/CheckTickets/RangeDatePickerCustom';
import {
  Button,
  createDateObjectByTimezone,
} from '../Agent/TransactionHistoryOverview/components/Filter';
import useWindowSize from 'app/hooks/useWindowSize';
import StyledSearchInput from '../NewAccount/components/StyledSearchInput';
import StyledSelect from '../NewAccount/components/StyledSelect';
import { convertVItoEN } from '../NewAccount/CheckTickets/helper';
import StyledButton from 'app/components/StyledButton';
import useTitleSize from 'app/components/StyledTable/useTitleSize';
import StyledTable from 'app/components/StyledTable';
import StyledPagination from 'app/components/StyledPagination';
import { SmallTitle } from '../Agent/TransactionHistoryOverview/components/OverviewScreen';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { useGlobalFunction } from '../App/hooks/useGlobalFunction';
import { StyledStack } from 'app/components/Stack';
import useFetchData, { CallbackOptions } from 'app/hooks/useFetchData';
import { exportTopupHistory, getTopupHistory } from 'app/services/authApi';
import TooltipParagraph from '../Catalog/tooltipParagraph';
import { formatNumber } from 'utils/helper';

type FilterFormValues = {
  startDate: Moment | null;
  endDate: Moment | null;
  transactionNo: string;
  transactionType: string;
  status: string;
  page: number;
  limit: number;
};

const transformFilterValues = (values: FilterFormValues) => ({
  startDate: values.startDate?.format('YYYY-MM-DD'),
  endDate: values.endDate?.format('YYYY-MM-DD'),
  transactionNo: values.transactionNo.trim() || undefined,
  transactionType:
    !values.transactionType || values.transactionType === 'All'
      ? undefined
      : values.transactionType,
  status: !values.status || values.status === 'All' ? undefined : values.status,
  page: values.page,
  limit: values.limit,
});

const momentTz = moment().tz('Asia/Ho_Chi_Minh');

const TopupHistory: React.FC = () => {
  const translations = useSelector(selectTranslations);
  const history = useHistory();
  const tableRef = useRef<HTMLDivElement>();
  const { windowSize } = useWindowSize();
  const [pageLoading, setPageLoading] = useState(false);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const { device } = useWindowSize();
  const isDesktop = device === 'desktop';

  const filterForm = useForm<FilterFormValues>({
    defaultValues: {
      startDate: momentTz.clone().startOf('month'),
      endDate: momentTz.clone(),
      transactionNo: '',
      transactionType: undefined,
      status: 'success',
      page: 1,
      limit: 20,
    },
  });

  const filterRef = useRef<FilterFormValues>(filterForm.getValues());

  const payloadData = useMemo(
    () => transformFilterValues(filterRef.current),
    [],
  );

  const { result: topupHistory, onFetchData: onFetchTopupHistory } =
    useFetchData({
      actionApi: getTopupHistory,
      payloadData,
      onBefore: () => {
        setPageLoading(true);
      },
      onFinally: () => {
        setPageLoading(false);
      },
    });

  const paginator = topupHistory?.paginator;

  const titleColumnSizes = useTitleSize({
    title: [
      'b2b.txt.agent-table-order',
      'b2b.txt.time',
      'b2b.txt.order-id',
      'b2b.txt.topupOrderCode',
      'b2b.txt.transaction-type',
      'b2b.txt.amount',
      'b2b.txt.status',
      'b2b.form.tourNotes',
    ],
  });

  const columns = useMemo(
    () => [
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.agent-table-order']}
          </div>
        ),
        key: 'order',
        dataIndex: 'order',
        render: value => {
          return (
            <div
              style={{
                textAlign: 'left',
                minWidth: titleColumnSizes['b2b.txt.agent-table-order'],
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.time']}
          </div>
        ),
        key: 'time',
        dataIndex: 'time',
        render: value => {
          const [time, date] = value?.split(' ');

          return (
            <div
              style={{
                textAlign: 'left',
                minWidth: titleColumnSizes['b2b.txt.time'],
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.order-id']}
          </div>
        ),
        key: 'orderId',
        dataIndex: 'orderId',
        render: value => {
          return (
            <div
              style={{
                textAlign: 'center',
                minWidth: titleColumnSizes['b2b.txt.order-id'],
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.topupOrderCode']}
          </div>
        ),
        key: 'orderCode',
        dataIndex: 'orderCode',
        render: value => {
          return (
            <div
              style={{
                textAlign: 'center',
                minWidth: titleColumnSizes['b2b.txt.topupOrderCode'],
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.transaction-type']}
          </div>
        ),
        key: 'transactionType',
        dataIndex: 'transactionType',
        render: value => {
          return (
            <div
              style={{
                textAlign: 'left',
                minWidth: titleColumnSizes['b2b.txt.transaction-type'],
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'right' }}>
            {translations?.['b2b.txt.amount']}
          </div>
        ),
        key: 'amount',
        dataIndex: 'amount',
        render: value => {
          return (
            <div
              style={{
                textAlign: 'right',
                minWidth: titleColumnSizes['b2b.txt.amount'],
              }}
            >
              {!!value || value === 0 || value === '0'
                ? `${formatNumber(value).replaceAll(',', '.')}đ`
                : null}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.status']}
          </div>
        ),
        key: 'status',
        dataIndex: 'status',
        render: value => {
          return (
            <div
              style={{
                textAlign: 'center',
                minWidth: titleColumnSizes['b2b.txt.status'],
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.form.tourNotes']}
          </div>
        ),
        key: 'note',
        dataIndex: 'note',
        render: value => {
          return (
            <div
              style={{
                textAlign: 'left',
              }}
            >
              <TooltipParagraph
                ellipsis={{ rows: 2 }}
                style={{
                  width: 180,
                  minWidth: titleColumnSizes['b2b.form.tourNotes'],
                  marginBottom: 0,
                  textAlign: 'left',
                }}
              >
                {value}
              </TooltipParagraph>
            </div>
          );
        },
      },
    ],
    [titleColumnSizes, translations],
  );

  const dataSource = useMemo(() => {
    const paginator = topupHistory?.paginator;

    if (!paginator) return [];

    return topupHistory?.data?.map((item, idx) => ({
      order: (paginator.current - 1) * paginator.numItemsPerPage + idx + 1,
      time: item.creationDate,
      orderId: item.documentNumber,
      orderCode: item?.transactionNo || '',
      transactionType: item.depositTypeName,
      amount: item.amount,
      status: item.statusName,
      note: item.comment,
    }));
  }, [topupHistory]);

  const disabledExportExcel = !dataSource?.length;

  const handleChangePage = (page: number, pageSize: number) => {
    filterForm.setValue('page', page);
    filterForm.setValue('limit', pageSize);
    filterRef.current = { ...filterRef.current, page, limit: pageSize };

    onFetchTopupHistory({
      payloadData: transformFilterValues(filterRef.current),
      callback: {
        onSuccess: () => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
      },
    });
  };

  const handleExportExcel = async () => {
    try {
      setPageLoading(true);
      const { page, limit, ...params } = transformFilterValues(
        filterRef.current,
      );

      const res = await exportTopupHistory(JSON.parse(JSON.stringify(params)));

      if (res?.data?.success) {
        window.location.href = res?.data?.data?.path;
      }
    } catch (error: any) {
      onOpenDialogNoti({
        type: 'ERROR',
        content:
          error instanceof AxiosError
            ? error?.response?.data?.message
            : error?.message,
        isShow: true,
        title: '',
        onCloseDialog: onCloseDialogNoti,
      });
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <Layout
      title={translations ? translations['b2b.txt.topUpHistory'] : ''}
      menuActive={ACCOUNT_MNG_MENU.AUTO_DEPOSIT}
      useBackArrow
      onBack={() => {
        const historyState = history.location.state as
          | { from: string }
          | undefined;
        if (historyState && historyState['from']) {
          history.goBack();
        } else {
          history.replace('/auto-deposit');
        }
      }}
    >
      <LoadingContainer loading={pageLoading}>
        <div
          style={{
            display: 'flex',
            flexDirection: isDesktop ? 'column' : 'row',
            justifyContent: isDesktop ? 'flex-start' : 'flex-end',
            marginBottom: isDesktop ? 0 : 16,
            gap: isDesktop ? 0 : 8,
          }}
        >
          <FormProvider {...filterForm}>
            <Filter
              filterRef={filterRef}
              tableRef={tableRef}
              onFetchTopupHistory={onFetchTopupHistory}
            />
          </FormProvider>
          <div
            style={{
              marginTop: isDesktop ? 16 : 0,
              marginBottom: isDesktop ? 16 : 0,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={handleExportExcel}
              disabled={disabledExportExcel}
              style={{
                filter: `grayscale(${disabledExportExcel ? '1' : '0'})`,
                cursor: disabledExportExcel ? 'not-allowed' : 'pointer',
                ...(isDesktop
                  ? {}
                  : {
                      height: 41,
                      width: 41,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgb(237, 237, 237)',
                      borderRadius: 10,
                    }),
              }}
            >
              <ExportExcel />
              {windowSize.width >= 1280 && (
                <SmallTitle
                  style={{
                    margin: 0,
                    color: '#fab317',
                    fontSize: 14,
                    width: 'max-content',
                    marginLeft: 6,
                    fontWeight: 400,
                  }}
                >
                  {translations?.['b2b.txt.export-excel']}
                </SmallTitle>
              )}
            </Button>
          </div>
        </div>
        <div style={{ width: '100%' }}>
          <StyledTable
            tableRef={tableRef}
            dataSource={dataSource ?? []}
            columns={columns}
            tableProps={{
              pagination: false,
              scroll: {
                x: dataSource?.length ? 'auto' : 'max-content',
                y: dataSource?.length ? '50vh' : undefined,
              },
            }}
            styleSlots={{
              antTableContainer: {
                overflow: 'hidden',
              },
              antTableHeader: {
                border: 'none !important',
                borderRadius: '0 !important',
              },
              tableCSS: {
                borderTopLeftRadius: !dataSource?.length ? '' : '0 !important',
                borderTopRightRadius: !dataSource?.length ? '' : '0 !important',
              },
              antTableBody: {
                paddingBottom: 10,
              },
              antTableExpandedRowFixed: {
                minHeight: 110,
              },
            }}
          />
          {paginator && (
            <>
              <StyledPagination
                showSizeChanger
                style={{ margin: 'auto', marginTop: 24 }}
                current={filterForm.getValues('page') || 1}
                total={paginator?.totalCount || 0}
                pageSize={filterForm.getValues('limit') || 20}
                onChange={handleChangePage}
                pageSizeOptions={['20', '50', '100']}
              />
            </>
          )}
        </div>
      </LoadingContainer>
    </Layout>
  );
};

export default TopupHistory;

const Filter: React.FC<{
  filterRef: MutableRefObject<FilterFormValues>;
  tableRef: MutableRefObject<HTMLDivElement | undefined>;
  onFetchTopupHistory: (
    additionalOptions?:
      | {
          payloadData?: any;
          callback?: CallbackOptions<any> | undefined;
        }
      | undefined,
  ) => Promise<any>;
}> = ({ filterRef, tableRef, onFetchTopupHistory }) => {
  const { device } = useWindowSize();
  const isDesktop = device === 'desktop';
  const [menuVisible, setMenuVisible] = useState(false);

  return isDesktop ? (
    <FilterComponent
      filterRef={filterRef}
      tableRef={tableRef}
      onFetchTopupHistory={onFetchTopupHistory}
    />
  ) : (
    <Dropdown
      visible={menuVisible}
      trigger={['click']}
      onVisibleChange={visible => setMenuVisible(visible)}
      getPopupContainer={trigger => trigger.parentNode as HTMLElement}
      overlayStyle={{ zIndex: 3 }}
      overlay={
        <StyledStack
          style={{
            background: 'white',
            width: 292,
            padding: 16,
            borderRadius: 10,
            boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
          }}
        >
          <FilterComponent
            filterRef={filterRef}
            tableRef={tableRef}
            onSearch={() => setMenuVisible(false)}
            onFetchTopupHistory={onFetchTopupHistory}
          />
        </StyledStack>
      }
    >
      <StyledButton
        bgColor="#fff"
        style={{
          border: '1px solid #EDEDED',
          borderRadius: 10,
          width: 41,
          height: 41,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        <FilterIcon width={16} height={16} color="#000" />
      </StyledButton>
    </Dropdown>
  );
};

const FilterComponent: React.FC<{
  onSearch?: (data: FilterFormValues) => void;
  filterRef: MutableRefObject<FilterFormValues>;
  tableRef: MutableRefObject<HTMLDivElement | undefined>;
  onFetchTopupHistory: (
    additionalOptions?:
      | {
          payloadData?: any;
          callback?: CallbackOptions<any> | undefined;
        }
      | undefined,
  ) => Promise<any>;
}> = ({ onSearch, filterRef, tableRef, onFetchTopupHistory }) => {
  const translations = useSelector(selectTranslations);
  const { control, setValue, getValues, handleSubmit } =
    useFormContext<FilterFormValues>();
  const { device } = useWindowSize();
  const isDesktop = device === 'desktop';

  const handleSearch = (data: FilterFormValues) => {
    onSearch?.(data);
    setValue('page', 1);
    filterRef.current = {
      ...data,
      page: 1,
    };
    onFetchTopupHistory({
      payloadData: transformFilterValues(filterRef.current),
      callback: {
        onSuccess: () => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
      },
    });
  };

  return (
    <Row gutter={isDesktop ? [16, 16] : [8, 8]}>
      <RangeDatePickerContainer span={isDesktop ? 6 : 24}>
        <RangeDatePickerCustom
          placeholder={
            translations ? translations['b2b.txt.selectDateRange'] : ''
          }
          startDateInit={createDateObjectByTimezone(getValues('startDate'))}
          endDateInit={createDateObjectByTimezone(getValues('endDate'))}
          onChange={([start, end]) => {
            setValue('startDate', start ? moment(start) : null);
            setValue('endDate', end ? moment(end) : null);
          }}
          minDate={null}
        />
      </RangeDatePickerContainer>
      <Col span={isDesktop ? 5 : 24}>
        <Controller
          control={control}
          name="transactionNo"
          render={({ field }) => (
            <StyledSearchInput
              placeholder={
                translations ? translations['b2b.txt.enterTransactionCode'] : ''
              }
              width="100%"
              {...field}
              onBlur={() => {
                setValue('transactionNo', getValues('transactionNo').trim());
              }}
            />
          )}
        />
      </Col>
      <Col span={isDesktop ? 5 : 24}>
        <Controller
          control={control}
          name="transactionType"
          render={({ field }) => (
            <StyledSelect
              filterOption={(input, option) =>
                (convertVItoEN(option?.label) ?? '').includes(
                  convertVItoEN(input),
                )
              }
              showSearch
              suffixIcon={<ArrowDownIcon />}
              placeholder={
                translations ? translations['b2b.file.classify'] : ''
              }
              options={[
                { label: translations?.['b2b.text.all'] ?? '', value: 'All' },
                {
                  label: translations?.['b2b.option.autoDeposit'],
                  value: 'BN',
                },
                {
                  label: translations?.['b2b.option.manualDeposit'],
                  value: 'SAP',
                },
              ]}
              allowClear
              style={{ width: '100%' }}
              {...field}
            />
          )}
        />
      </Col>
      <Col span={isDesktop ? 5 : 24}>
        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <StyledSelect
              filterOption={(input, option) =>
                (convertVItoEN(option?.label) ?? '').includes(
                  convertVItoEN(input),
                )
              }
              showSearch
              suffixIcon={<ArrowDownIcon />}
              placeholder={translations ? translations['b2b.txt.status'] : ''}
              options={[
                {
                  label: translations?.['b2b.option.success'] ?? '',
                  value: 'success',
                },
                {
                  label: translations?.['b2b.option.waitForPayment'] ?? '',
                  value: 'pending',
                },
                {
                  label: translations?.['b2b.option.failed'] ?? '',
                  value: 'cancelled',
                },
              ]}
              style={{ width: '100%' }}
              {...field}
            />
          )}
        />
      </Col>
      <Col span={isDesktop ? 3 : 24}>
        <StyledButton
          onClick={handleSubmit(handleSearch)}
          style={{
            background: '#FFFFFF',
            border: '1px solid #F8B117',
            color: '#F8B117',
            width: '100%',
            height: 44,
            flex: 1,
          }}
        >
          {translations ? translations['b2c.txt.search'] : ''}
        </StyledButton>
      </Col>
    </Row>
  );
};

const RangeDatePickerContainer = styled(Col)`
  .range-picker-container {
    width: 100%;
  }
  .react-datepicker__close-icon {
    right: 5px;
  }
  .date-picker {
    height: 44px;
  }
`;
